import React, { useEffect, useState, useContext, useCallback } from 'react';
import {useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Module from './Module';
import axiosInstance from '../../../axiosInstance';
import { UserContext } from '../../../UserContext'; 
import { calculateScaledScore } from './Score';
import './SAThook.css';
import Lottie from 'lottie-react';
import ProgressBar from './ProgressBar';
import animationData from '../Assets/animationData.json'; 

const API_URL = process.env.REACT_APP_API_URL;
const DEFAULT_QUIZ_ID = 'QUIZ_1'; 

const SAThook = () => {
    const { user, email, viewState, setViewState, isStudent, setIsStudent, quizFinished, setQuizFinished } = useContext(UserContext);
    const navigate = useNavigate();
    const [modules, setModules] = useState([]);
    const [activeModuleIndex, setActiveModuleIndex] = useState(0);
    const moduleTitle = `Module ${activeModuleIndex + 1}`.toUpperCase();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [questionId, setQuestionId] = useState(null);
    const [question, setQuestion] = useState([]);
    const [selectedOption, setSelectedOption] = useState({});
    const [progressCount, setProgressCount] = useState(0);
    const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
    const [totalAnsweredQuestions, setTotalAnsweredQuestions] = useState(0); 
    const [correctAnswersInModule, setCorrectAnswersInModule] = useState(0);
    const [skippedQuestions, setSkippedQuestions] = useState(new Set());
    const [hasAnswered, setHasAnswered] = useState(new Set());
    const [showExplanation, setShowExplanation] = useState(false);
    const [explanationText, setExplanationText] = useState('');

    const [isCorrect, setIsCorrect] = useState(0);
    const [correctAnswer, setCorrectAnswer] = useState(0);
    const [answerDetails, setAnswerDetails] = useState({    
        timeLeft: null,
        totalPauseDuration: null
    });

    const [totalCorrectAnswers, setTotalCorrectAnswers] = useState(0);
    
    const [scaledScoreState, setScaledScore] = useState(null); 
    const [quizID, setQuizID] = useState(DEFAULT_QUIZ_ID);
    const [noMoreQuizzes, setNoMoreQuizzes] = useState(false); 
    const [pauseCount, setPauseCount] = useState(0);
    const [totalPauseDuration, setTotalPauseDuration] = useState(0); 
    const [extraTimeAfforded, setExtraTimeAfforded] = useState(0);
    const [timeLeft, setTimeLeft] = useState(1920); 
    const [lastQuizCompleted, setLastQuizCompleted] = useState(null);
    const [isRestoration, setIsRestoration] = useState(false);
     
    const [showRoboAnimation, setShowRoboAnimation] = useState(false);
    const [hasSeenRoboAnimation, setHasSeenRoboAnimation] = useState(false);
    const [roboTriggerCount, setRoboTriggerCount] = useState(null);

    const roboMessages = {
        1: `While practice quizzes and prep tools are valuable, they are just one piece of the puzzle. To truly excel, expose yourself to a wide variety of technical, scientific, and academic literature. Start with topics that genuinely interest you, and do a deep dive. As your knowledge grows, you'll naturally see how different concepts interconnect—building a richer understanding that will boost your critical reading skills far beyond the test.`,
        10: `You just answered 10 questions. Just showing up to do the work is half the battle. Keep up the momentum! <strong><a href="/login-signup" class="cta-link">Signup</a> for a new account to save your work, access to free supplemental lessons per question type, and print your certified transcript upon course completion.</strong>`
    };


    const [animationTimeout, setAnimationTimeout] = useState(null);

    const onSetSelectedOption = (officialAnswer) => {
        setSelectedOption(officialAnswer);
        
    };
    
    const onSetSkippedQuestions = (updatedSkippedQuestions) => {
        setSkippedQuestions((prevSkipped) => {
            const updatedSkipped = new Set(prevSkipped);
    
            answeredQuestions.forEach((answeredQuestionIndex) => {
                if (updatedSkipped.has(answeredQuestionIndex)) {
                    updatedSkipped.delete(answeredQuestionIndex); // 
                }
            });
    
        
            updatedSkippedQuestions.forEach((questionIndex) => {
                if (!answeredQuestions.has(questionIndex)) {
                    updatedSkipped.add(questionIndex);
                }
            });
    
          
            return updatedSkipped;
        });
    };

     

   useEffect(() => {
        const hasSeen = localStorage.getItem('hasSeenRoboAnimation');
        if (hasSeen) {
            setHasSeenRoboAnimation(true);
        }
    }, []);

    // NEW: Show animation after first answer with a delay
    useEffect(() => {
        if (totalAnsweredQuestions === 1 && !hasSeenRoboAnimation) {
            // Clear any existing timeout
            if (animationTimeout) {
                clearTimeout(animationTimeout);
            }
            
            // Set a timeout to show the animation after a brief delay
            const timeout = setTimeout(() => {
                setRoboTriggerCount(1);
                setShowRoboAnimation(true);
            }, 500); // Small delay to ensure the answer is processed first
            
            setAnimationTimeout(timeout);
        }
        if (totalAnsweredQuestions === 10) {
            if (animationTimeout) {
                clearTimeout(animationTimeout);
            }

            const timeout = setTimeout(() => {
                setRoboTriggerCount(10);
                setShowRoboAnimation(true);
            }, 500);
            setAnimationTimeout(timeout);
        }

    }, [totalAnsweredQuestions, hasSeenRoboAnimation]);

    const handleCloseRoboAnimation = () => {
        setShowRoboAnimation(false);

        if (roboTriggerCount === 1) {
            setHasSeenRoboAnimation(true);
            localStorage.setItem('hasSeenRoboAnimation', 'true');
        }

        if (animationTimeout) {
            clearTimeout(animationTimeout);
        }
    };


    

    useEffect(() => {
        const email = localStorage.getItem('userEmail');
        const progressKey = email ? `${email}_quizProgress` : 'quizProgress';
        const savedProgress = localStorage.getItem(progressKey);

        if (isStudent === true) {
            if (email) {
            // If user has email (logged in) but we're in SAT hook, redirect to login-signup
            navigate('/login-signup');
            return;
            } else if (savedProgress) {
            // If no email but progress exists in localStorage, redirect to login-signup
            navigate('/login-signup');
            return;
            }
            
            // If no email and no saved progress, continue with SAT hook
            fetchQuizData();
        } else {
            // For professionals, just fetch quiz data
            fetchQuizData();
        }
    }, [isStudent, navigate]);
    

   
      
      // Function to fetch quiz data
      const fetchQuizData = () => {
        axiosInstance.get(`${API_URL}/api/sat-quiz/hook`, {
          params: { quizID: 'QUIZ_1' }
        })
          .then(response => {
            const parsedQuestions = parseQuestions(response.data);
            setModules(parsedQuestions);
            setActiveModuleIndex(0);
          })
          .catch(error => {
            console.error("Error fetching quiz questions:", error);
          });
      };
      
    
    const parseQuestions = (data) => {
        
        return data.modules.map((moduleQuestions, moduleIndex) => {
            return moduleQuestions.map(item => ({
                question: item.question,
                passage: item.passage || '',
                options: item.options.map(opt => ({
                    label: opt.label,
                    option: opt.option,
                    explanation: opt.explanation || ''
                })),
                correctAnswer: item.correctAnswer
            }));
        });
    };
    
    

        const handleModuleComplete = () => {
            if (activeModuleIndex === 0){
                setExtraTimeAfforded(totalPauseDuration / 60);
                setProgressCount(totalAnsweredQuestions);
                setTimeLeft(1920);
                
                const nextModuleIndex = 1;
                const moduleQuestions = modules[nextModuleIndex] || [];
                setActiveModuleIndex(nextModuleIndex);
                setQuestion(moduleQuestions);
    
                const nextModuleTitle = `Module ${nextModuleIndex + 1}`.toUpperCase();
            }
        
            if (activeModuleIndex < modules.length - 1) {
               
                setProgressCount(totalAnsweredQuestions);
               
                const nextModuleIndex = activeModuleIndex + 1;
                setActiveModuleIndex(nextModuleIndex);
    
                const nextModuleTitle = `Module ${nextModuleIndex + 1}`.toUpperCase();
    
                setIsRestoration(false);
                 
            } else {
                setQuizFinished(true);
            }
        };
        


    useEffect(() => {
        const answeredCount = answeredQuestions.size; 
        setTotalAnsweredQuestions(answeredCount); 
    }, [answeredQuestions]);

    const handleAnsweredQuestions = ({
        timeLeft, 
        incrementTotalAnswered        
    }) => {
        setAnsweredQuestions(prev => {
            const updatedAnswered = new Set(prev);
            updatedAnswered.add(currentQuestionIndex); 
            return updatedAnswered; 
        });
        setHasAnswered(prev => {
            const updatedHasAnswered = new Set(prev);
            updatedHasAnswered.add(currentQuestionIndex); 
            return updatedHasAnswered; 
        });
        setAnswerDetails({
            timeLeft,    
        });
         if (incrementTotalAnswered) {
        setTotalAnsweredQuestions(prev => {
            const updatedTotal = prev + 1;
            return updatedTotal;
        });
    }
    
        setSkippedQuestions(prevSkipped => {
            const updatedSkipped = new Set(prevSkipped);
            const questionKey = `${activeModuleIndex}-${currentQuestionIndex}`;
            updatedSkipped.delete(questionKey); 
            return updatedSkipped; 
        });
    };

        
    useEffect(() => {
        const answeredCount = answeredQuestions.size; 
        setTotalAnsweredQuestions(answeredCount); 
    }, [answeredQuestions]);


    const onCorrectAnswer = () => {
        setTotalCorrectAnswers(prev => prev + 1); 
    };

    const handlePauseUpdate = (newPauseCount, newTotalPauseDuration) => {
       
        if (newPauseCount !== null && newPauseCount !== undefined) {
            setPauseCount(newPauseCount); 
        }

        if (newTotalPauseDuration !== null && newTotalPauseDuration !== undefined) {
            setTotalPauseDuration(newTotalPauseDuration); 
            
        }
    };

    useEffect(() => {
        if (quizFinished) {
            const finalScaledScore = totalCorrectAnswers ? calculateScaledScore(totalCorrectAnswers) : 0;

            const progressData = {
                quizFinished: true,
                scaledScore: finalScaledScore,
                lastQuizCompleted: quizID,  
            };

            const progressKey = user?.email ? `${user.email}_quizProgress` : 'quizProgress';
            localStorage.setItem(progressKey, JSON.stringify(progressData));
        }
    }, [quizFinished, totalCorrectAnswers, quizID]);

    const handleQuizFinished = async () => {
        try {
        setTimeLeft(null);
        const finalScaledScore = calculateScaledScore(totalCorrectAnswers);
        setScaledScore(finalScaledScore);
        } catch (error) {
            console.error('Error saving progress or fetching next quiz:', error);
        }
    };

    
    const handleLoginSignup = () => {
        setViewState('scoreConfirmation')
        navigate('/login-signup');
    };


    return (
        <div className="quiz-container">
            <Helmet>
                <title>SAT Practice Test | Contranorm Media & Production</title>
                <meta
                    name="description"
                    content="Gain continuing education credits (CEU) as you practice for the digital SAT. These interactive quizzes are designed to help you improve your score by 120 points."
                />
                <meta
                    name="keywords"
                    content="SAT preparation, how to improve SAT score, free SAT practice, free SAT quiz, SAT practice activities, continuing education credits"
                />
                <link rel="canonical" href="https://contranormdigital.com/sat-hook" />
                </Helmet>

            <div className="confetti-container"></div>
            <ProgressBar 
                answeredCount={progressCount + totalAnsweredQuestions}  
                total={modules.reduce((acc, module) => acc + module.length, 0)}  
                quizFinished={quizFinished}
            />
            {showRoboAnimation && (
            <div className="robo-animation-notice show"> {/* Changed from just 'show' to include both classes */}
                <div className="popup-header">
                    <h3><strong>Study Tips Just for You!</strong></h3>
                    <button className="close-btn" onClick={handleCloseRoboAnimation} aria-label="Close tip">
                        &times;
                    </button>
                </div>
                
                <div className="robot-animation">
                    <Lottie 
                        animationData={animationData}
                        loop={true}
                        style={{ width: '160px', height: '160px' }}
                        className="floating"
                    />
                </div>
                
                <p dangerouslySetInnerHTML={{ __html: roboMessages[roboTriggerCount] }} />

            </div>
        )}
            {noMoreQuizzes ? (
                <div className="message">
                    <h2>No more quizzes available!</h2>
                </div>
            ) : !quizFinished && modules.length > 0 ? (
                <Module
                    key={activeModuleIndex}
                    moduleTitle={moduleTitle}
                    currentQuestionIndex={currentQuestionIndex}
                    setCurrentQuestionIndex={setCurrentQuestionIndex}
                    moduleTime={timeLeft}
                    onTimeLeftChange={setTimeLeft} 
                    questions={modules[activeModuleIndex]}
                    onAnswerSelected={handleAnsweredQuestions}
                    onCorrectAnswer={onCorrectAnswer} 
                    onComplete={handleModuleComplete}
                    onPauseUpdate={handlePauseUpdate}
                    pauseCount={pauseCount}
                    totalPauseDuration={totalPauseDuration}
                    isRestoration={isRestoration} 
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    onSetSelectedOption={onSetSelectedOption}
                    answeredQuestions={answeredQuestions} 
                    setAnsweredQuestions={setAnsweredQuestions} 
                    hasAnswered={hasAnswered} 
                    setHasAnswered={setHasAnswered}  
                    showExplanation={showExplanation} 
                    setShowExplanation={setShowExplanation} 
                    explanationText={explanationText} 
                    setExplanationText={setExplanationText} 
                    question={question}                    
                    setQuestion={setQuestion}              
                    isCorrect={isCorrect}                  
                    setIsCorrect={setIsCorrect}            
                    correctAnswer={correctAnswer}          
                    setCorrectAnswer={setCorrectAnswer}  
                    correctAnswersInModule={correctAnswersInModule}  
                    setCorrectAnswersInModule={setCorrectAnswersInModule} 
                    skippedQuestions={skippedQuestions} 
                    setSkippedQuestions={setSkippedQuestions}
                    onSetSkippedQuestions={onSetSkippedQuestions}
                    questionId={questionId}
                    setQuestionId={setQuestionId}
                    
                />
           
            ) : quizFinished && viewState === 'scoreConfirmation' ?  (
                <>
                <div className="happy-animation">
                  <Lottie animationData={animationData} loop={true} />
                </div>
                <div className="message">
                  <h2>Scaled Score</h2>
                  <p></p>
                  <div className="score">
                    <h2>{calculateScaledScore(totalCorrectAnswers)}</h2>
                  </div>
                   {/* Benefits Section */}
                <div className="benefits-section">
                  <h2>Save Your Results & Get:</h2>
                  <ul className="benefits-list">
                    <li>
                      <span className="check-icon">✔</span> <strong>Free</strong>&nbsp;Adaptive Testing
                    </li>
                    <li>
                      <span className="check-icon">✔</span> <strong>Free</strong>&nbsp;SAT-Equivalent Tests
                    </li>
                    <li>
                      <span className="check-icon">✔</span> <strong>Free</strong>&nbsp;Certificate of Completion
                    </li>
                    <li>
                      <span className="check-icon">✔</span> Official SAT-Equivalent Scores
                    </li>
                    <li>
                      <span className="check-icon">✔</span> Continuing Education Credits
                    </li>
                   
                    <li>
                      <span className="check-icon">✔</span> Certified Transcript Upon Completion
                    </li>
                  </ul>
                </div>
                
                <button className="login-signup-button" onClick={handleLoginSignup}>
                  Save & Continue
                </button>
            
                </div>
            
            
              </>
            ) : (
                <p>Loading questions...</p>
            )}
            
            
        </div>
    );
};

export default SAThook;
